import * as React from "react";
import { useState } from "react";
import {
  Button,
  Box,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";

import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import CustomizedDataGrid from "../../Components/CustomizedDataGrid";
import PaymentFormPage from "../Payment-Form";
import { colorForName, initialsFor } from "../../PatientsPage/Components/avatarUtils";
import axios from '../../../Configs/axiosInstance';
import { MAIN_URL } from "../../../Configs/Urls";
import { getStoredPageSize, setStoredPageSize } from "../../../Configs/paginationStorage";

// yyyy-mm-dd (from the backend) -> dd-mm-yyyy, for display.
const formatDate = (dateString) => {
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

// One of the three "Today / This Week / This Month" summary tiles.
function StatTile({ icon, color, label, amount }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: 'divider', flex: 1, minWidth: 220 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: `${color}1F`, color, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ₹{amount.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function MainGrid() {
  const [Payments, setPayments] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [totals, setTotals] = React.useState({ today: 0, week: 0, month: 0 });

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: getStoredPageSize("payments"),
  });
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setStoredPageSize("payments", model.pageSize);
  };
  const [searchTerm, setSearchTerm] = useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [Deleteloading, setDeleteLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  const [PaymentId, setPaymentId] = React.useState(null);

  // Fetch exactly one page from the backend — either the plain listing or
  // the search endpoint, depending on whether there's a search term. Both
  // return the same { data, meta } shape.
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const trimmedSearch = searchTerm.trim();
      const endpoint = trimmedSearch ? "payments/search" : "payments";
      const params = {
        page: paginationModel.page + 1, // backend pages are 1-indexed
        per_page: paginationModel.pageSize,
        ...(trimmedSearch ? { query: trimmedSearch } : {}),
      };

      const response = await axios.get(`${MAIN_URL}${endpoint}`, { params });

      setPayments(response.data.data.map((payment) => ({
        ...payment,
        formattedDate: formatDate(payment.paymentDate),
      })));
      setRowCount(response.data.meta.total);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setError("Failed to fetch payments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Today/week/month totals are computed server-side over the whole table,
  // independent of whichever page the list is currently showing.
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${MAIN_URL}payments/stats`);
      setTotals(response.data);
    } catch (error) {
      console.error("Error fetching payment stats:", error);
    }
  };

  // Refetch the list whenever the page/page-size changes, or 450ms after
  // the admin stops typing in the search box.
  React.useEffect(() => {
    const handler = setTimeout(fetchPayments, searchTerm ? 450 : 0);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchTerm]);

  React.useEffect(() => {
    fetchStats();
  }, []);

  // A new search resets to page 1 — otherwise the admin can land on a page
  // that no longer exists for the narrower result set.
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleEdit = (row) => {
    setPaymentId(row.id);
  };

  const handleDelete = (row) => {
    setPaymentId(row.id);
    setOpenDeleteDialog(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${MAIN_URL}payments/${PaymentId}`);
      setSnackbarMessage("Payment Deleted Successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchPayments(); // re-fetch so pagination/total stays accurate
      fetchStats();
    } catch (error) {
      console.error("Error deleting payments:", error);
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error Deleting payment. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setPaymentId(null);
      setDeleteLoading(false);
    }
  };

  const onCloseForm = () => {
    setPaymentId(null);
    fetchPayments();
    fetchStats();
  };

  const columns = [
    {
      field: "id",
      headerName: "Payment ID",
      width: 110,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "payerName",
      headerName: "Patient's Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, height: "100%" }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: colorForName(params.value) }}>
            {initialsFor(params.value)}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>₹{params.value}</Box>
      ),
    },
    {
      field: "formattedDate",
      headerName: "Payment Date",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "paymentType",
      headerName: "Payment Type",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={params.value}
            size="small"
            color={params.value === "Cash" ? "success" : params.value === "Online" ? "info" : "default"}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: "purpose",
      headerName: "Purpose",
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const isEditing = PaymentId !== null && !openDeleteDialog;

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      {isEditing ? (
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={onCloseForm}
          sx={{ mb: 2, textTransform: "none", fontWeight: 600 }}
        >
          Back to Payment List
        </Button>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <StatTile icon={<TodayRoundedIcon />} color="#1E88E5" label="Today's Total Payments" amount={totals.today} />
            <StatTile icon={<DateRangeRoundedIcon />} color="#8E24AA" label="This Week's Total Payments" amount={totals.week} />
            <StatTile icon={<CalendarMonthRoundedIcon />} color="#43A047" label="This Month's Total Payments" amount={totals.month} />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2.5,
              borderRadius: 3,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Payment List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rowCount} {rowCount === 1 ? "payment" : "payments"} in total
              </Typography>
            </Box>

            <TextField
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
              variant="outlined"
              placeholder="Search by patient name"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </>
      )}

      {error && (
        <Typography component="h2" variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {isEditing ? (
        <PaymentFormPage paymentId={PaymentId} onCloseForm={onCloseForm} />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}>
          <CustomizedDataGrid
            rows={Payments}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setPaymentId(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this payment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setPaymentId(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={Deleteloading}
            variant="contained"
            color="error"
            size="small"
          >
            {Deleteloading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
